<template>
  <div>
    <div class="label">{{ $t('CampaignForm_NativeInline_Parameters') }}</div>
    <div class="json-parameters">
      <form-item
        :key="index"
        v-for="(item, index) in value.formSettings"
        :label="trimLabel(index)"
      >
        <snippet-input
          v-if="checkType(index, inAppFormTypes.PLAINTEXT)"
          v-model="value.formValues[index]"
          :emoji="false"
          :snippets="false"
        />
        <el-select
          class="form-input"
          :multiple="checkType(index, inAppFormTypes.MULTISELECT)"
          v-model="value.formValues[index]"
          :placeholder="$t('CampaignForm_Select')"
          v-if="
            checkType(index, inAppFormTypes.SELECT) ||
              checkType(index, inAppFormTypes.MULTISELECT)
          "
        >
          <el-option
            v-for="option in value.formSettings[index].options"
            :key="option.key"
            :label="option.key"
            :value="option.value"
          >
          </el-option>
        </el-select>

        <el-input-number
          class="form-input"
          controls-position="right"
          v-model="value.formValues[index]"
          v-if="checkType(index, inAppFormTypes.NUMBER)"
        />
        <div class="datetimepicker" v-else-if="inAppFormTypes.DATETIME">
          <el-date-picker
            v-if="datetime[index]"
            format="MMMM d, yyyy"
            value-format="yyyy-MM-dd"
            v-model="datetime[index].date"
            class="date-input"
            @change="dateChanged(index, $event)"
          />
          <el-time-picker
            v-if="datetime[index]"
            format="HH:mm"
            value-format="HH:mm"
            v-model="datetime[index].time"
            @change="timeChanged(index, $event)"
          />
          <el-select
            v-if="datetime[index]"
            v-model="datetime[index].timezone"
            filterable
          >
            <el-option
              v-for="(label, val) in timezones"
              :key="val"
              :value="val"
              :label="label"
            >
              <span v-tooltip>{{ label }}</span>
            </el-option>
          </el-select>
        </div>
      </form-item>
    </div>
  </div>
</template>

<script>
import { inAppFormTypes } from '@/modules/campaign-manage/enum/InAppTypesEnum'
import FormItem from '@/common/components/form/FormItem'
import moment from 'moment'
import 'moment-timezone'
import { mapGetters } from 'vuex'
import SnippetInput from '@/modules/campaign-manage/components/SnippetInput'
export default {
  name: 'InlineForm',
  components: { SnippetInput, FormItem },
  data() {
    return {
      inAppFormTypes,
      now: moment()
        .tz(this.$store.state.app.project.timezone)
        .startOf('date'),
      datetime: {}
    }
  },
  props: {
    value: {
      type: Object,
      required: true
    }
  },
  computed: {
    ...mapGetters(['timezones'])
  },
  mounted() {
    delete this.timezones['']
    this.setupDateTime()
  },
  methods: {
    dateChanged(key, newValue) {
      if (newValue === null) {
        this.$nextTick(() => {
          this.datetime[key].date = moment()
            .tz(this.datetime[key].timezone)
            .format('Y-MM-DD')
        })
      }
    },
    timeChanged(key, newValue) {
      if (newValue === null) {
        this.$nextTick(() => {
          this.datetime[key].time = '00:00'
        })
      }
    },
    setupDateTime() {
      try {
        Object.keys(this.value.formSettings).forEach(item => {
          if (this.checkType(item, inAppFormTypes.DATETIME)) {
            const tz = this.value.formValues[item]
              ? this.value.formValues[item].timezone
              : this.$store.state.app.project.timezone

            const dateObj = {
              date: moment
                .tz(
                  this.value.formValues[item]
                    ? `${this.value.formValues[item].date} ${this.value.formValues[item].time}`
                    : this.now,
                  tz
                )
                .format('YYYY-MM-DD'),
              time: moment
                .tz(
                  this.value.formValues[item]
                    ? `${this.value.formValues[item].date} ${this.value.formValues[item].time}`
                    : this.now,
                  tz
                )
                .format('HH:mm'),
              timezone: tz
            }

            this.$set(this.datetime, item, dateObj)
          }
        })
      } catch (e) {}
    },
    checkType(item, type) {
      return this.value.formSettings[item].type === type
    },
    trimLabel(label) {
      return label
        .replace('{{', '')
        .replace('}}', '')
        .trim()
        .split(/-|_/)
        .map(str => str.slice(0, 1).toUpperCase() + str.slice(1))
        .join(' ')
    },
    convertValueJSONString(value) {
      let newVal = JSON.stringify(value)
      try {
        if (typeof JSON.parse(value) === 'object') {
          newVal = value
        }
        // This catch is meant to be empty, in order to avoid JSON.parse to throw the error,
        // and allow to carry on with the following return value
      } catch (e) {}
      return newVal
    }
  },
  watch: {
    datetime: {
      handler: function(newVal) {
        Object.keys(this.datetime).forEach(item => {
          this.$set(this.value.formValues, item, newVal[item])
        })
      },
      deep: true
    },
    'value.formValues': {
      handler: function() {
        this.$set(this.value, 'form', this.value.formTemplate)
        Object.keys(this.value.formValues).forEach(key => {
          if (
            (!this.value.formValues[key] && this.value.formValues[key] !== 0) ||
            (Array.isArray(this.value.formValues[key]) &&
              !this.value.formValues[key].length)
          ) {
            return
          }

          let newVal = this.value.formValues[key]
          switch (this.value.formSettings[key].type) {
            case inAppFormTypes.SELECT:
              newVal = this.convertValueJSONString(this.value.formValues[key])
              break
            case inAppFormTypes.MULTISELECT:
              let option = this.value.formValues[key]
                .map(value => this.convertValueJSONString(value))
                .join(',')
              newVal = `[${option}]`
              break
            case inAppFormTypes.DATETIME:
              const newDateString =
                this.value.formValues[key].date &&
                this.value.formValues[key].time
                  ? moment
                      .tz(
                        `${this.value.formValues[key].date} ${this.value.formValues[key].time}`,
                        this.value.formValues[key].timezone
                      )
                      .utc()
                      .format()
                  : ''
              newVal = `"${newDateString}"`
              break
            case inAppFormTypes.PLAINTEXT:
              newVal = `"${this.value.formValues[key]}"`
          }
          const fieldReg = new RegExp(
            `{{(\\s*)field\\[\\"${key
              .replace('{{', '')
              .replace('}}', '')}\\"\\](\\s*)}}`,
            'g'
          )
          this.value.form = this.value.form.replace(fieldReg, newVal)
        })
      },
      deep: true
    }
  }
}
</script>

<style scoped lang="scss">
.label {
  color: #5c6374;
  font-size: 14px;
}
.json-parameters {
  padding: 5px 10px 0;
}
div.datetimepicker {
  display: flex;
  gap: 12px;
}
</style>



// WEBPACK FOOTER //
// src/modules/campaign-manage/components/setup/InlineForm.vue